import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ChakraProvider } from "@chakra-ui/react";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Label from "./pages/Label";
import Home from "./pages/Home";
import Release from "./pages/Release";
import Report from "./pages/Report";
import ReferralSettlement from "./pages/ReferralSettlement";
import Notice from "./pages/Notice";
import NoticeDetail from "./pages/NoticeDetail";
import ResetPassword from "./pages/ResetPassword";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import AlbumDetail from "./pages/AlbumDetail";
import CollaborationLayout from "./components/layouts/CollaborationLayout";
import CollaborationHome from "./pages/MusicCollaboration/Home";
import ChannelManagement from "./pages/MusicCollaboration/ChannelManagement";
import AlbumList from "./pages/MusicCollaboration/AlbumList";
import Dashboard from "./pages/MusicCollaboration/Dashboard";
import RevenueByVideo from "./pages/MusicCollaboration/RevenueByVideo";
import RevenueByChannel from "./pages/MusicCollaboration/RevenueByChannel";
import SettlementManagement from "./pages/MusicCollaboration/SettlementManagement";
import TermsOfService from "./pages/policy/TermsOfService";
import PrivacyPolicy from "./pages/policy/PrivacyPolicy";
import DigitalContentTerms from "./pages/policy/DigitalContentTerms";
import MyPage from "./pages/MyPage";
import Partners from "./pages/MusicCollaboration/Partners";
import LicensedVideoRequest from "./pages/MusicCollaboration/LicensedVideoRequest";
import AdminLayout from "./components/admin/AdminLayout";
import Users from "./pages/admin/members/Users";
import Contracts from "./pages/admin/shorts/Contracts";
import CompletedUserSettlement from "./pages/admin/settlement/CompletedUser";
import PendingUserSettlement from "./pages/admin/settlement/PendingUser";
import FeeManagement from "./pages/admin/settlement/FeeManagement";
import LicenseVideos from "./pages/admin/shorts/LicenseVideos";
import Channels from "./pages/admin/shorts/Channels";
import AlbumsList from "./pages/admin/albums/AlbumsList";
import Ownership from "./pages/admin/albums/Ownership";
import Tracks from "./pages/admin/albums/tracks";
import Labels from "./pages/admin/albums/labels";
import Musicians from "./pages/admin/albums/musicians";
import ShortsAlbums from "./pages/admin/shorts/albums";
import List from "./pages/admin/shorts/List";
import Youtube from "./pages/admin/settlement/youtube";
import PopupList from "./pages/admin/popup/PopupList";
import PopupForm from "./pages/admin/popup/PopupForm";
import NoticeList from "./pages/admin/notice/NoticeList";
import NoticeForm from "./pages/admin/notice/NoticeForm";
import PartnersList from "./pages/admin/partners/PartnersList";
import PartnersListMonthly from "./pages/admin/partners/PartnersListMonthly";
import ContractAgreement from "./pages/ContractAgreement";

// 보호된 라우트 컴포넌트
const ProtectedRoute = ({ children }) => {
  const { user, userProfile } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  // is_not_contract가 null이면 운영 동의서 페이지로 리다이렉트
  if (!userProfile) {
    return children;
  } else if (
    userProfile?.is_not_contract === null ||
    userProfile?.is_not_contract === undefined
  ) {
    return <Navigate to="/contract-agreement" />;
  }
  return <Layout>{children}</Layout>;
};

// 공개 라우트 컴포넌트
const PublicRoute = ({ children }) => {
  return <Layout>{children}</Layout>;
};

// 협업 라우트 컴포넌트
const CollaborationRoute = ({ children }) => {
  const { user, userProfile } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  // is_not_contract가 null이면 운영 동의서 페이지로 리다이렉트
  if (
    userProfile?.is_not_contract === null ||
    userProfile?.is_not_contract === undefined
  ) {
    return <Navigate to="/contract-agreement" />;
  }
  // is_not_contract가 false이면 리포트 페이지로 리다이렉트
  if (userProfile?.is_not_contract === false) {
    return <Navigate to="/report" />;
  }
  return <CollaborationLayout>{children}</CollaborationLayout>;
};

// 리포트 라우트 컴포넌트 (is_not_contract=false일 때만 접근 가능)
const ReportRoute = ({ children }) => {
  const { user, userProfile } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  // is_not_contract가 null이면 운영 동의서 페이지로 리다이렉트
  if (
    userProfile?.is_not_contract === null ||
    userProfile?.is_not_contract === undefined
  ) {
    return <Navigate to="/contract-agreement" />;
  }
  // is_not_contract가 true이거나 false일 때 리포트 페이지 접근 가능
  return <Layout>{children}</Layout>;
};

// 동의서 라우트 컴포넌트 (is_not_contract가 null 또는 false일 때 접근 가능)
const ContractAgreementRoute = ({ children }) => {
  const { user, userProfile } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  // is_not_contract가 null, undefined, false일 때만 접근 가능
  // true일 때는 이미 동의했으므로 접근하지 못하게 함
  if (userProfile?.is_not_contract === true) {
    return <Navigate to="/" />;
  }
  return <Layout>{children}</Layout>;
};

// 어드민 라우트 컴포넌트
const AdminRoute = ({ children }) => {
  const { user, userProfile } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  // admin 컬럼은 "true"/"True" 등 문자열로 저장된다 (DB의 is_admin()과 동일 기준)
  if (String(userProfile?.admin).trim().toLowerCase() !== "true") {
    return <Navigate to="/" />;
  }
  return <AdminLayout>{children}</AdminLayout>;
};

function App() {
  return (
    <ChakraProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/signup"
              element={
                <PublicRoute>
                  <Signup />
                </PublicRoute>
              }
            />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/contract-agreement"
              element={
                <ContractAgreementRoute>
                  <ContractAgreement />
                </ContractAgreementRoute>
              }
            />
            <Route
              path="/label"
              element={
                <ProtectedRoute>
                  <Label />
                </ProtectedRoute>
              }
            />
            <Route
              path="/release"
              element={
                <ProtectedRoute>
                  <Release />
                </ProtectedRoute>
              }
            />
            <Route
              path="/report"
              element={
                <ReportRoute>
                  <Report />
                </ReportRoute>
              }
            />
            <Route
              path="/referral-settlement"
              element={
                <ProtectedRoute>
                  <ReferralSettlement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notice"
              element={
                <PublicRoute>
                  <Notice />
                </PublicRoute>
              }
            />
            <Route
              path="/notice/:id"
              element={
                <PublicRoute>
                  <NoticeDetail />
                </PublicRoute>
              }
            />
            <Route
              path="/"
              element={
                <PublicRoute>
                  <Home />
                </PublicRoute>
              }
            />
            <Route
              path="/album/:id"
              element={
                <ProtectedRoute>
                  <AlbumDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/laeeblyforshorts"
              element={
                <CollaborationRoute>
                  <CollaborationHome />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/dashboard"
              element={
                <CollaborationRoute>
                  <Dashboard />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/channels"
              element={
                <CollaborationRoute>
                  <ChannelManagement />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/albums"
              element={
                <CollaborationRoute>
                  <AlbumList />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/revenue-by-video"
              element={
                <CollaborationRoute>
                  <RevenueByVideo />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/revenue-by-channel"
              element={
                <CollaborationRoute>
                  <RevenueByChannel />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/settlements"
              element={
                <CollaborationRoute>
                  <SettlementManagement />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/partners"
              element={
                <CollaborationRoute>
                  <Partners />
                </CollaborationRoute>
              }
            />
            <Route
              path="/collaboration/licensed-video-request"
              element={
                <CollaborationRoute>
                  <LicensedVideoRequest />
                </CollaborationRoute>
              }
            />
            <Route
              path="/terms"
              element={
                <PublicRoute>
                  <TermsOfService />
                </PublicRoute>
              }
            />
            <Route
              path="/privacy"
              element={
                <PublicRoute>
                  <PrivacyPolicy />
                </PublicRoute>
              }
            />
            <Route
              path="/digital-content-terms"
              element={
                <PublicRoute>
                  <DigitalContentTerms />
                </PublicRoute>
              }
            />
            <Route
              path="/mypage"
              element={
                <ProtectedRoute>
                  <MyPage />
                </ProtectedRoute>
              }
            />
            {/* 어드민 라우트 */}
            <Route
              path="/admin/*"
              element={
                <AdminRoute>
                  <Routes>
                    <Route
                      path="/"
                      element={<Navigate to="/admin/dashboard" replace />}
                    />
                    <Route path="/dashboard" element={<div>대시보드</div>} />
                    <Route path="/members/users" element={<Users />} />
                    <Route
                      path="/members/membership"
                      element={<div>멤버쉽 회원</div>}
                    />
                    <Route path="/albums/list" element={<AlbumsList />} />
                    <Route path="/shorts/ownership" element={<Ownership />} />
                    <Route path="/albums/tracks" element={<Tracks />} />
                    <Route path="/albums/musicians" element={<Musicians />} />
                    <Route path="/albums/labels" element={<Labels />} />
                    <Route path="/shorts/contracts" element={<Contracts />} />
                    <Route path="/shorts/channels" element={<Channels />} />
                    <Route path="/shorts/albums" element={<ShortsAlbums />} />
                    <Route
                      path="/shorts/channels"
                      element={<div>쇼츠 채널</div>}
                    />
                    <Route path="/shorts/list" element={<List />} />
                    <Route path="/shorts/license" element={<LicenseVideos />} />
                    <Route
                      path="/settlement/commission"
                      element={<FeeManagement />}
                    />
                    <Route
                      path="/settlement/pending-users"
                      element={<PendingUserSettlement />}
                    />
                    <Route
                      path="/settlement/completed-users"
                      element={<CompletedUserSettlement />}
                    />
                    <Route
                      path="/settlement/pending-partners"
                      element={<div>예정 파트너</div>}
                    />
                    <Route
                      path="/settlement/completed-partners"
                      element={<div>완료 파트너</div>}
                    />
                    <Route path="/settlement/youtube" element={<Youtube />} />
                    <Route path="/popup/list" element={<PopupList />} />
                    <Route path="/popup/create" element={<PopupForm />} />
                    <Route path="/popup/edit/:id" element={<PopupForm />} />
                    <Route path="/notice/list" element={<NoticeList />} />
                    <Route path="/notice/create" element={<NoticeForm />} />
                    <Route path="/notice/edit/:id" element={<NoticeForm />} />
                    <Route path="/partners/list" element={<PartnersList />} />
                    <Route
                      path="/partners/monthly"
                      element={<PartnersListMonthly />}
                    />
                    <Route
                      path="/support/notices"
                      element={<div>공지사항</div>}
                    />
                    <Route path="/support/faq" element={<div>FAQ</div>} />
                    <Route
                      path="/support/inquiries"
                      element={<div>사용자 문의</div>}
                    />
                    <Route path="/settings" element={<div>환경설정</div>} />
                    <Route path="/albums/ownership" element={<Ownership />} />
                  </Routes>
                </AdminRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
